import type { TFunction } from 'i18next';
import { ErrorObject } from '../types/errorhandling.types';

type AxiosShapedError = { response?: { data?: unknown } };

const isErrorObject = (value: unknown): value is ErrorObject => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const entries = Object.entries(value as Record<string, unknown>);
  return (
    entries.length > 0 &&
    entries.every(
      ([, messages]) =>
        Array.isArray(messages) &&
        messages.length > 0 &&
        messages.every((message) => typeof message === 'string')
    )
  );
};

/**
 * Normalises whatever the API layer threw into the `{ field: [message] }` shape
 * ErrorBanner renders. `makeApiRequest` rethrows the response body directly, but
 * plain axios errors still carry it under `response.data`.
 * Returns undefined when the payload isn't a field/message map (network errors,
 * FastAPI's `{ detail: [...] }`, ...) so callers can fall back to a generic message.
 */
export const toFieldErrors = (error: unknown): ErrorObject | undefined => {
  const body = (error as AxiosShapedError)?.response?.data ?? error;
  return isErrorObject(body) ? body : undefined;
};

/**
 * Validation messages come back from the API in English; `branding.errors` is
 * the shared dictionary that maps them to a translated sentence.
 */
export const translateFieldError = (t: TFunction, message: string): string =>
  t(`branding.errors.${message}`, { defaultValue: message });
